/**
 * exportUtils.js
 * Shared XLSX workbook builders for the Tasket task export feature.
 *
 * Two exported functions:
 *
 *   buildSpecificTaskWorkbook(bundle)
 *     Builds a 6-sheet workbook for one task:
 *       1. Task Details
 *       2. Subtasks
 *       3. Comments
 *       4. Attachments
 *       5. Reports
 *       6. Audit History
 *
 *   buildOverallWorkbook(bundle)
 *     Builds a 7-sheet workbook for many tasks:
 *       1. Task Overview  ← hierarchical parent→subtask tree (human-readable)
 *       2. Task Details
 *       3. Subtasks       ← every row carries parent Task ID
 *       4. Comments       ← every row carries parent Task ID
 *       5. Attachments    ← every row carries parent Task ID
 *       6. Reports        ← every row carries parent Task ID
 *       7. Audit History  ← every row carries parent Task ID
 *
 * Both functions return an XLSX workbook object (from the "xlsx" package).
 * Callers write it to disk with:
 *   import * as XLSX from 'xlsx';
 *   const wb = buildSpecificTaskWorkbook(bundle);
 *   XLSX.writeFile(wb, filename);
 *
 * No S3 file contents are included — attachments are metadata-only.
 */

import * as XLSX from 'xlsx';

// ── Formatting helpers ─────────────────────────────────────────────────────────

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(v); }
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};

const fmtBytes = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const str = (v) => (v == null || v === '' ? '—' : String(v));

// Apply column widths based on the data in a worksheet
function autoWidth(ws, rows) {
  if (!rows || !rows.length) return;
  const keys = Object.keys(rows[0]);
  const widths = keys.map((k) => {
    const header = String(k).length;
    const maxData = rows.reduce((m, r) => Math.max(m, String(r[k] ?? '').length), 0);
    return { wch: Math.min(Math.max(header, maxData) + 2, 80) };
  });
  ws['!cols'] = widths;
}

function makeSheet(rows) {
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  return ws;
}

// ── Row builders ──────────────────────────────────────────────────────────────

function taskRows(tasks) {
  return tasks.map((t) => ({
    'Task ID':        t.id,
    'Task Type':      str(t.task_type_code || t.task_type),
    'Project':        str(t.project_name),
    'Title':          str(t.title),
    'Description':    str(t.description),
    'Status':         str(t.status),
    'Priority':       str(t.priority),
    'Assigned To':    str(t.assignee_name),
    'Assignee Email': str(t.assignee_email),
    'Created By':     str(t.created_by_name),
    'Due Date':       fmtDate(t.due_date),
    'Created Date':   fmtDateTime(t.created_at),
    'Updated Date':   fmtDateTime(t.updated_at),
    'Closed Date':    fmtDate(t.closed_at),
    'Feedback':       str(t.feedback),
  }));
}

function subtaskRows(subtasks) {
  return subtasks.map((s) => ({
    'Subtask ID':     s.id,
    'Parent Task ID': s.task_id,
    'Title':          str(s.title),
    'Description':    str(s.description),
    'Status':         str(s.status),
    'Priority':       str(s.priority),
    'Assigned To':    str(s.assignee_name),
    'Created By':     str(s.created_by_name),
    'Due Date':       fmtDate(s.due_date),
    'Created Date':   fmtDateTime(s.created_at),
    'Updated Date':   fmtDateTime(s.updated_at),
    'Closed Date':    fmtDate(s.closed_at),
    'Feedback':       str(s.feedback),
  }));
}

function commentRows(comments) {
  return comments.map((c) => ({
    'Comment ID':        c.id,
    'Parent Task ID':    c.task_id,
    'Subtask ID':        c.subtask_id ?? '—',
    'Parent Comment ID': c.parent_id   ?? '—',
    'Author':            str(c.author_name),
    'Message':           str(c.message),
    'Edited':            c.is_edited ? 'Yes' : 'No',
    'Created Date':      fmtDateTime(c.created_at),
    'Updated Date':      fmtDateTime(c.updated_at),
  }));
}

function attachmentRows(attachments) {
  return attachments.map((a) => ({
    'Attachment ID':  a.id,
    'Parent Task ID': a.task_id,
    'Subtask ID':     a.subtask_id ?? '—',
    'File Name':      str(a.original_name),
    'File Type':      str(a.mime_type),
    'File Size':      fmtBytes(a.file_size),
    'Uploaded By':    str(a.uploaded_by_name),
    'S3 Key / Path':  str(a.storage_key),
    'Uploaded Date':  fmtDateTime(a.created_at),
  }));
}

function reportRows(reports) {
  return reports.map((r) => ({
    'Report ID':       r.id,
    'Parent Task ID':  r.task_id,
    'Report Title':    str(r.report_title),
    'Content':         str(r.report_content),
    'Report Date':     fmtDate(r.report_date),
    'Submitted By':    str(r.submitted_by_name),
    'Submitted At':    fmtDateTime(r.submitted_at),
    'Review Comments': str(r.review_comments),
    'Reviewed At':     fmtDateTime(r.reviewed_at),
  }));
}

function auditRows(taskAudit, subtaskAudit) {
  const rows = [];
  for (const a of (taskAudit || [])) {
    rows.push({
      'Audit ID':      a.id,
      'Parent Task ID':a.task_id,
      'Subtask ID':    '—',
      'Type':          'Task',
      'Actor':         str(a.actor_name),
      'Action':        str(a.action),
      'Module':        str(a.module),
      'Old Priority':  str(a.old_priority),
      'New Priority':  str(a.new_priority),
      'Old Due Date':  fmtDate(a.old_due_date),
      'New Due Date':  fmtDate(a.new_due_date),
      'Old Value':     a.old_value ? JSON.stringify(a.old_value) : '—',
      'New Value':     a.new_value ? JSON.stringify(a.new_value) : '—',
      'Feedback':      str(a.feedback),
      'Date':          fmtDateTime(a.created_at),
    });
  }
  for (const s of (subtaskAudit || [])) {
    rows.push({
      'Audit ID':      s.id,
      'Parent Task ID':s.task_id ?? '—',
      'Subtask ID':    s.subtask_id,
      'Type':          'Subtask',
      'Actor':         str(s.actor_name),
      'Action':        str(s.action),
      'Module':        'SUBTASK',
      'Old Priority':  '—',
      'New Priority':  '—',
      'Old Due Date':  '—',
      'New Due Date':  '—',
      'Old Value':     '—',
      'New Value':     s.meta ? (typeof s.meta === 'string' ? s.meta : JSON.stringify(s.meta)) : '—',
      'Feedback':      '—',
      'Date':          fmtDateTime(s.created_at),
    });
  }
  return rows;
}

// ── Task Overview sheet (hierarchical, Overall Export only) ─────────────────────

function overviewRows(tasks, subtasks) {
  // Build a map: task_id → subtask[]
  const subtaskMap = {};
  for (const s of (subtasks || [])) {
    if (!subtaskMap[s.task_id]) subtaskMap[s.task_id] = [];
    subtaskMap[s.task_id].push(s);
  }

  const rows = [];
  for (const t of (tasks || [])) {
    rows.push({
      'Level':        'Task',
      'Task ID':      t.id,
      'Subtask ID':   '—',
      'Task / Subtask Name': str(t.title),
      'Task Type':    str(t.task_type_code || t.task_type),
      'Project':      str(t.project_name),
      'Assigned To':  str(t.assignee_name),
      'Status':       str(t.status),
      'Priority':     str(t.priority),
      'Due Date':     fmtDate(t.due_date),
      'Created Date': fmtDateTime(t.created_at),
      'Updated Date': fmtDateTime(t.updated_at),
    });
    for (const s of (subtaskMap[t.id] || [])) {
      rows.push({
        'Level':        'Subtask',
        'Task ID':      t.id,
        'Subtask ID':   s.id,
        'Task / Subtask Name': str(s.title),
        'Task Type':    '—',
        'Project':      str(t.project_name),
        'Assigned To':  str(s.assignee_name),
        'Status':       str(s.status),
        'Priority':     str(s.priority),
        'Due Date':     fmtDate(s.due_date),
        'Created Date': fmtDateTime(s.created_at),
        'Updated Date': fmtDateTime(s.updated_at),
      });
    }
  }
  return rows;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build a 6-sheet workbook for one specific task.
 * @param {Object} bundle  { tasks, subtasks, comments, attachments, reports, taskAudit, subtaskAudit }
 * @returns XLSX workbook
 */
export function buildSpecificTaskWorkbook(bundle) {
  const { tasks = [], subtasks = [], comments = [], attachments = [], reports = [], taskAudit = [], subtaskAudit = [] } = bundle;

  const wb = XLSX.utils.book_new();

  const addSheet = (name, rows) => {
    const safeRows = rows.length ? rows : [{ '(no records)': '' }];
    const ws = makeSheet(safeRows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  addSheet('Task Details',  taskRows(tasks));
  addSheet('Subtasks',      subtaskRows(subtasks));
  addSheet('Comments',      commentRows(comments));
  addSheet('Attachments',   attachmentRows(attachments));
  addSheet('Reports',       reportRows(reports));
  addSheet('Audit History', auditRows(taskAudit, subtaskAudit));

  return wb;
}

/**
 * Build a 7-sheet hierarchical workbook for an overall (multi-task) export.
 * Sheet 1 is the human-readable Task Overview hierarchy.
 * Sheets 2–7 are the complete detail records, each with parent Task ID.
 * @param {Object} bundle  { tasks, subtasks, comments, attachments, reports, taskAudit, subtaskAudit }
 * @returns XLSX workbook
 */
export function buildOverallWorkbook(bundle) {
  const { tasks = [], subtasks = [], comments = [], attachments = [], reports = [], taskAudit = [], subtaskAudit = [] } = bundle;

  const wb = XLSX.utils.book_new();

  const addSheet = (name, rows) => {
    const safeRows = rows.length ? rows : [{ '(no records)': '' }];
    const ws = makeSheet(safeRows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  // Sheet 1 — hierarchical overview (most important for readability)
  addSheet('Task Overview',  overviewRows(tasks, subtasks));

  // Sheets 2–7 — complete detail records
  addSheet('Task Details',  taskRows(tasks));
  addSheet('Subtasks',      subtaskRows(subtasks));
  addSheet('Comments',      commentRows(comments));
  addSheet('Attachments',   attachmentRows(attachments));
  addSheet('Reports',       reportRows(reports));
  addSheet('Audit History', auditRows(taskAudit, subtaskAudit));

  return wb;
}
